import { getLanguageCodeByHost, LOCALES } from '@benzinga/translate';

export const isTrustedDomain = (hostname: string): boolean => {
  return hostname.endsWith('benzinga.com') || hostname.endsWith('zingbot.bz');
};

export const isProDomain = (url: string): boolean => {
  return (
    url.includes('pro.benzinga.com') ||
    url.includes('pro.zingbot.bz') ||
    url.includes('probeta.benzinga.com') ||
    url.includes('probeta.zingbot.bz')
  );
};

export const isGlobalDomain = (url: string): boolean => {
  if (!isTrustedDomain(url)) return false;

  const TRUSTED_URL = new URL(url);

  return getLanguageCodeByHost(TRUSTED_URL.hostname) !== LOCALES.EN;
};

export const formatRedirectUrl = (url: string, defaultUrl: string): string => {
  if (!url) {
    return defaultUrl;
  }

  // decode url string which may be url encoded
  let redirectUrl = decodeURIComponent(url);

  // Strip special non-alphanumeric characters at the start of redirectUrl
  redirectUrl = redirectUrl.replace(/^[^a-zA-Z0-9]+/, '');

  try {
    // create url from absolute path
    const absoluteUrl = new URL(redirectUrl);

    // if the domain is not trusted, redirect to default url
    if (!isTrustedDomain(absoluteUrl.hostname)) {
      redirectUrl = defaultUrl;
    }
    // if the url is valid, use it
    redirectUrl = absoluteUrl.href;
  } catch (_err) {
    // if the url is not an absolute path, it'll fail and we attempt relative flow
    try {
      // create url from relative path
      const relativeUrl = new URL(redirectUrl, window.location.origin);

      // if the domain is not trusted, redirect to default url
      if (!isTrustedDomain(relativeUrl.hostname)) {
        redirectUrl = defaultUrl;
      }
      // if the url is valid, use it
      redirectUrl = relativeUrl.href;
    } catch (_err) {
      // if all else fails, redirect to default url
      redirectUrl = defaultUrl;
    }
  }

  return redirectUrl;
};

export const preventRedirectToLogin = (url: string, defaultUrl: string): string => {
  if (
    !url ||
    url.endsWith('/login') ||
    url.endsWith('/register') ||
    url.endsWith('/login/') ||
    url.endsWith('/register/') ||
    url.includes('/login?') ||
    url.includes('/register?')
  ) {
    return defaultUrl;
  }
  return url;
};

export const edgePaywallCopy = {
  'analyst-predictions': {
    button: ['Unlock Access Here', 'Reveal Top-Rated Stocks'],
    headline: "Top-Rated Stocks You Can't Afford to Miss",
    subhead:
      'Unlock real-time analytics, expert recommendations, and ad-free experience. Make informed decisions faster.',
    utm: 'top-rated-stocks-you-cant-afford-to-miss',
  },
  'analyst-ratings': {
    button: ['Unlock Access Here', 'Reveal Top-Rated Stocks'],
    headline: "Top-Rated Stocks You Can't Afford to Miss",
    subhead:
      'Unlock real-time analytics, expert recommendations, and ad-free experience. Make informed decisions faster.',
    utm: 'top-rated-stocks-you-cant-afford-to-miss',
  },
  'default-article': {
    button: ['Access My Picks Now'],
    headline: 'Get 5 Bonus Stock Picks Every Week',
    subhead:
      'Expert-picked trade recommendations, in-depth analysis, and premium tools are available now when you upgrade to Benzinga Edge.',
    utm: 'get-5-bonus-stock-picks-every-week',
  },
  earnings: {
    button: ['Unlock Access Now', 'Unlock Earnings Edge Now'],
    headline: 'Get an Edge This Earnings Season with Daily Stock Ideas',
    subhead:
      "Access Benzinga's proprietary ratings, instant earnings alerts, and daily expert insights. Stay ahead of the market.",
    utm: 'get-an-edge-this-earnings-season',
  },
  'gov-trades': {
    button: ['Unlock Access Now', 'Reveal Government Trades'],
    headline: 'Track Congress: Uncover Market-Moving Trades',
    subhead:
      'Daily updates on what Congress members are buying and selling. Follow the money that shapes policies and markets.',
    utm: 'track-congress-uncover-market-moving-trades',
  },
  'insider-trades': {
    button: ['Access Insider Trades Now', 'Get Access Here'],
    headline: 'Insider Intel: Get 5 Premium Trades Weekly',
    subhead:
      'Follow the smart money. Get expert-picked trade ideas and proprietary tools to capitalize on insider movements.',
    utm: 'insider-intel-get-5-premium-trades-weekly',
  },
  'stock-of-the-day': {
    button: ['Access My Picks Now'],
    headline: 'Get 5 Bonus Stock Picks Every Week',
    subhead:
      'Join Benzinga Edge and unlock access to daily expert-recommended picks, actionable insights, and proprietary investing tools today.',
    utm: 'get-5-bonus-stock-picks-every-week',
  },
  'unusual-options-activity': {
    button: ['Get Access Here', 'Reveal Unusual Options Now'],
    headline: 'Unusual Options Alert: Spot Hidden Opportunities',
    subhead: "Gain exclusive access to Benzinga Edge's premium tools. Identify potential breakouts before they happen.",
    utm: 'unusual-options-alert-spot-hidden-opportunities',
  },
  'whisper-index': {
    button: 'Get Access Now',
    headline: "Whisper Alert: Insider Picks You Won't Find Elsewhere",
    subhead:
      'Unlock hidden opportunities with our exclusive stock recommendations and expert analysis. Enjoy an ad-light experience.',
    utm: 'whisper-alert-insider-picks-you-wont-find-elsewhere',
  },
};

export const getEdgePaywallUtmSource = ({ edgeIteration, headline, type }) => {
  return `${headline}-${edgeIteration}-${type}`;
};

export const wallStreetCopy = {
  'analyst-color': {
    headline: 'Discover Your <br/> Next Winning Trade',
    points: [
      'Expert ratings on 1000+ stocks',
      'Price target changes in real-time',
      'Instant alerts on major upgrades and downgrades',
      'Easy access to the best-performing analysts',
      "Can't miss trend changes",
      'Plus, more premium content, reports & tools',
    ],
    subhead: 'UNLOCK EXPERT STOCK RATINGS',
    subscriberType: 'Feature',
    text: 'Save 43% when you join today for less than $11 a month',
    utmSource: 'analyst-color',
  },
  'analyst-predictions': {
    headline: 'Discover Your <br/> Next Winning Trade',
    points: [
      'Expert ratings on 1000+ stocks',
      'Price target changes in real-time',
      'Instant alerts on major upgrades and downgrades',
      'Easy access to the best-performing analysts',
      "Can't miss trend changes",
      'Plus, more premium content, reports & tools',
    ],
    subhead: 'UNLOCK EXPERT STOCK RATINGS',
    subscriberType: 'Feature',
    text: 'Save 43% when you join today for less than $11 a month',
    utmSource: 'analyst-predictions',
  },
  'analyst-ratings': {
    altCheckout: true,
    headline: 'Discover Your <br/> Next Winning Trade',
    points: [
      'Expert ratings on 1000+ stocks',
      'Price target changes in real-time',
      'Instant alerts on major upgrades and downgrades',
      'Easy access to the best-performing analysts',
      "Can't miss trend changes",
      'Plus, more premium content, reports & tools',
    ],
    subhead: 'UNLOCK EXPERT STOCK RATINGS',
    subscriberType: 'Feature',
    text: 'Save 43% when you join today for less than $11 a month',
    utmSource: 'PaywallAnalystStock',
  },
  earnings: {
    altCheckout: true,
    headline: 'Never Miss A <br/> Market Moving Report',
    points: [
      'Real-time earnings releases & alerts',
      'Tracking on beats & misses',
      'What Wall Street expects',
      'Post-earnings trends insights ',
      'Full earnings season calendar access',
      'Plus, more premium content, reports & tools',
    ],
    subhead: 'UNLOCK PREMIUM EARNINGS ALERTS',
    subscriberType: 'Feature',
    text: 'Save 43% when you join today for less than $11 a month',
    utmSource: 'PaywallEarnings',
  },
  'easy-income-portfolio': {
    altCheckout: true,
    headline: 'Without The <br/> Wall Street Price Tag',
    points: [
      "Full access to the 'Easy Income Portfolio'",
      'Real-time market updates for fast action',
      'Exclusive export insights',
      'Weekly hidden gem stock picks',
      'Professional-level research tools',
      'In-depth reports to spot emerging trends',
    ],
    subhead: 'WALL STREET LEVEL INTEL',
    text: 'Save 43% when you join today for less than $11 a month',
    utmSource: 'PaywallEasyIncome',
  },
  'perfect-stock-portfolio': {
    altCheckout: true,
    headline: 'Without The <br/> Wall Street Price Tag',
    points: [
      "Full access to the 'Perfect Stock Portfolio'",
      'Real-time market updates for fast action',
      'Exclusive export insights',
      'Weekly hidden gem stock picks',
      'Professional-level research tools',
      'In-depth reports to spot emerging trends',
    ],
    subhead: 'WALL STREET LEVEL INTEL',
    text: 'Save 43% when you join today for less than $11 a month',
    utmSource: 'PaywallPerfectStock',
  },
  'rankings-page': {
    altCheckout: true,
    headline: 'Get the Complete <br/> Picture on Any <br> Stock, Anytime',
    points: [
      'All Premium Content & Stories',
      'Real-Time Market Updates',
      'Weekly Hidden Gem Stock Picks',
      'Professional-Level Research Tools',
      'A 43% New Member Discount',
    ],
    subhead: 'Benzinga Edge Members Only',
    subscriberType: 'Feature',
    text: 'Save 43% when you join today for less than $11 a month',
    utmSource: 'rankingsPage',
  },
  'unusual-options-activity': {
    altCheckout: true,
    headline: "Spot Tomorrow's <br/> Biggest Moves Today",
    points: [
      'Alerts on big-money options sweeps',
      'Exclusive intel on unusual volume spikes',
      'Real-time signals when whales place major bets',
      'Bullish VS Bearish ratings',
      'Expert breakdowns on the biggest potential plays',
      'Plus, more premium content, reports & tools',
    ],
    subhead: 'UNLOCK UNUSUAL OPTIONS ALERTS',
    subscriberType: 'Feature',
    text: 'Save 43% when you join today for less than $11 a month',
    utmSource: 'PaywallUnusualOptions',
  },
};
